import { Sigma } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMultiAgentStore } from "@/stores/multiAgentStore";
import { useShallow } from "zustand/react/shallow";

function tokensHuman(n: number): string {
  if (!n) return "0";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

/** Always-visible chip showing live token usage (replaces cost display). */
export function CostMeter() {
  const { cost, runId, sessionId, history, sessionContext, thread } = useMultiAgentStore(
    useShallow((s) => ({
      cost: s.cost,
      runId: s.runId,
      sessionId: s.sessionId,
      history: s.history,
      sessionContext: s.sessionContext,
      thread: s.thread,
    }))
  );
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentTotal = cost ? cost.tokensIn + cost.tokensOut : 0;
  const sessionHistoricalTotal = useMemo(() => {
    if (!sessionId && !runId) return 0;
    return history
      .filter((run) => {
        const runSessionId = run.session_id || run.id;
        if (sessionId) return runSessionId === sessionId;
        return run.id === runId;
      })
      .filter((run) => run.id !== runId || !cost)
      .reduce((sum, run) => sum + (run.tokens_total || 0), 0);
  }, [cost, history, runId, sessionId]);
  const sessionTotal = sessionHistoricalTotal + currentTotal;
  const displayedTotal = sessionTotal || currentTotal;

  // Hide entirely when there's nothing to show (fresh session, no runs yet).
  const isEmpty = currentTotal === 0 && sessionTotal === 0;

  // Close popover on click-outside or Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    const onMouse = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onMouse);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onMouse);
    };
  }, [open]);

  const sessionRunCount = useMemo(() => {
    if (!sessionId) return runId ? 1 : 0;
    const ids = new Set(
      history
        .filter((run) => (run.session_id || run.id) === sessionId)
        .map((run) => run.id),
    );
    if (runId) ids.add(runId);
    return ids.size || (runId ? 1 : 0);
  }, [history, runId, sessionId]);

  if (isEmpty) return null;

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        className="ma-micro inline-flex items-center gap-1"
        style={{
          padding: "2px 8px",
          borderRadius: 999,
          background: "var(--ma-surface-soft)",
          color: "var(--ma-text-muted)",
          textTransform: "none",
          letterSpacing: 0,
          fontVariantNumeric: "tabular-nums",
        }}
        title="Session token usage and loaded context"
        onClick={() => setOpen((v) => !v)}
      >
        <Sigma className="h-3 w-3" />
        {tokensHuman(currentTotal)} current · {tokensHuman(displayedTotal)} session
      </button>

      {open && (
        <div
          className="ma-fade-up"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 8px)",
            zIndex: 30,
            width: 280,
            padding: "12px 14px",
            borderRadius: "var(--ma-radius-md)",
            background: "var(--ma-surface)",
            boxShadow: "var(--ma-shadow-md), inset 0 0 0 1px var(--ma-border)",
            color: "var(--ma-text)",
            fontSize: 12,
            lineHeight: 1.5,
          }}
        >
          <div className="ma-micro mb-2" style={{ color: "var(--ma-text-muted)" }}>
            Session usage
          </div>
          <div className="flex items-center justify-between">
            <span>Current run</span>
            <strong style={{ fontVariantNumeric: "tabular-nums" }}>{tokensHuman(currentTotal)}</strong>
          </div>
          <div className="flex items-center justify-between">
            <span>Whole session</span>
            <strong style={{ fontVariantNumeric: "tabular-nums" }}>{tokensHuman(displayedTotal)}</strong>
          </div>
          <div className="flex items-center justify-between">
            <span>Runs in session</span>
            <strong style={{ fontVariantNumeric: "tabular-nums" }}>{sessionRunCount}</strong>
          </div>
          <div className="mt-2" style={{ borderTop: "1px solid var(--ma-border)", paddingTop: 8 }}>
            {sessionContext ? (
              <>
                <div>
                  {sessionContext.turnCount} prior turn{sessionContext.turnCount === 1 ? "" : "s"} loaded
                  {thread.length > sessionContext.turnCount ? ` · ${thread.length} visible` : ""}
                </div>
                {sessionContext.artifactCount > 0 && (
                  <div>{sessionContext.artifactCount} artifact{sessionContext.artifactCount === 1 ? "" : "s"} available</div>
                )}
                {sessionContext.compressed ? (
                  <div style={{ color: "var(--ma-state-ok)" }}>
                    compressed
                    {typeof sessionContext.keptTurns === "number" && sessionContext.keptTurns > 0
                      ? ` · ${sessionContext.keptTurns} kept`
                      : ""}
                    {typeof sessionContext.compressedChars === "number" && sessionContext.compressedChars > 0
                      ? ` · ${sessionContext.compressedChars.toLocaleString()} chars`
                      : ""}
                  </div>
                ) : (
                  <div style={{ color: "var(--ma-text-muted)" }}>not compressed yet</div>
                )}
              </>
            ) : (
              <span style={{ color: "var(--ma-text-muted)" }}>
                Context loads on follow-up runs in this same session.
              </span>
            )}
          </div>
          {cost && (
            <div className="mt-2 ma-micro" style={{ textTransform: "none", letterSpacing: 0, color: "var(--ma-text-muted)" }}>
              {cost.tokensIn.toLocaleString()} in · {cost.tokensOut.toLocaleString()} out · {cost.toolCalls} tool calls
            </div>
          )}
        </div>
      )}
    </div>
  );
}
